import { useCallback, useContext, useMemo } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type Enums from '@core/types/api/Enums';
import { isValidUUID } from '@core/utils/uuid';

import { useConfirmDialog } from '../../providers/ConfirmProvider/useConfirmDialog';
import { useToast } from '../../providers/ToastProvider/useToast';
import type { MutationRouteBarrelTypes } from '../../utils/ApiClient';
import { useSlabMutation } from '../useSlabMutation';
import { ServerErrorMessage } from '../useSlabQuery';
import { createConfirmData } from './createConfirmData';
import { ARCHIVED_ENTITY_MAP, type Filters } from './createFilterOptions';

export type ArchiveKey =
  | 'POST archive activity by ID'
  | 'POST archive material by ID'
  | 'POST archive product by ID'
  | 'POST archive contact by ID'
  | 'POST archive company by ID'
  | 'POST archive mix by ID'
  | 'POST archive quote by ID'
  | 'POST archive project by ID'
  | 'POST archive quote config by ID'
  | 'POST archive project config by ID'
  | 'POST archive trucking type by ID'
  | 'POST archive reminder system setting by ID'
  | 'POST archive plant by ID'
  | 'POST archive competitor by ID'
  | 'POST archive fixed pricing by ID'
  | 'POST archive fixed pricing by ID'
  | 'POST archive adjusted pricing by ID'
  | 'POST archive imported pricing by ID';

export type AllowedArchived = Pick<MutationRouteBarrelTypes, ArchiveKey>;

type IsEmptyObject<T> = keyof T extends never ? true : false;

type ArchiveProps<T extends ArchiveKey> =
  IsEmptyObject<AllowedArchived[T]['args']['body']> extends true
    ? { id: string; body?: AllowedArchived[T]['args']['body'] }
    : { id: string; body: AllowedArchived[T]['args']['body'] };

export type ArchiveMutationProps<T extends ArchiveKey> = ArchiveProps<T> & {
  isArchived: boolean;
};

export interface UseArchiveReturn<T extends ArchiveKey> {
  confirmAndArchive(data: ArchiveMutationProps<T>): void;
  excludeArchivedFilter: Filters[T];
  isLoading: boolean;
  canArchive: boolean;
}

interface UseArchivedProps<T extends ArchiveKey> {
  entity: T;
  /** When set, only users with one of these roles may archive. Omit for unrestricted access. */
  allowedRoles?: Enums.RoleName[];
  onSuccess?(d: AllowedArchived[T]['returns']): void;
  restoreWarning?: string;
}

export const useArchiveManager = <T extends ArchiveKey>({
  entity,
  allowedRoles = [],
  onSuccess,
  restoreWarning,
}: UseArchivedProps<T>): UseArchiveReturn<T> => {
  const { subjectName, hideArchivedFilterBy } = ARCHIVED_ENTITY_MAP[entity];

  const confirm = useConfirmDialog();
  const toast = useToast({ subjectName });
  const archiveMutation = useSlabMutation(entity);
  const { userInfo } = useContext(SlabContext);

  const hasRoleRestriction = allowedRoles.length > 0;
  const canArchive = useMemo(
    () => !hasRoleRestriction || userInfo.hasRoles(allowedRoles),
    [allowedRoles, hasRoleRestriction, userInfo],
  );

  const assertCanArchive = useCallback(
    async (id: string): Promise<void> => {
      if (!isValidUUID(id)) {
        throw new Error('Invalid ID');
      }

      if (hasRoleRestriction && !canArchive) {
        throw new Error(
          `You cannot archive this. Your role must be one of: ${allowedRoles.join(' or ')}.`,
        );
      }
    },
    [allowedRoles, canArchive, hasRoleRestriction],
  );

  const handleArchiveMutation = useCallback(
    async ({ id, body = {} }: ArchiveProps<T>): Promise<AllowedArchived[T]['returns'] | null> => {
      try {
        await assertCanArchive(id);
        const data = await archiveMutation.mutateAsync({ args: { pathParams: { id }, body } });

        onSuccess?.(data);

        return data;
      } catch (error) {
        toast.error({ message: ServerErrorMessage(error, 'Failed to archive') });

        return null;
      }
    },
    [archiveMutation, assertCanArchive, onSuccess, toast],
  );

  const confirmAndArchive = useCallback(
    ({ id, isArchived, body = {} }: ArchiveMutationProps<T>): void => {
      const { title, description, submitVariant, cancelVariant, submitButton } = createConfirmData({
        isArchived,
        subjectName,
      });

      const finalDescription =
        !isArchived && restoreWarning ? `${description}. ${restoreWarning}` : description;

      const handleArchive = (): void => {
        handleArchiveMutation({ id, body });
      };

      confirm.alert({
        title,
        description: finalDescription,
        actions: [
          { label: 'Cancel', variant: cancelVariant },
          { label: submitButton, variant: submitVariant, onClick: handleArchive },
        ],
      });
    },
    [confirm, handleArchiveMutation, subjectName, restoreWarning],
  );

  return {
    canArchive,
    excludeArchivedFilter: !canArchive ? hideArchivedFilterBy : [],
    confirmAndArchive,
    isLoading: archiveMutation.isPending,
  };
};
